import type { IModularBenefitEnrolmentWindow } from '../../../types/modular-benefit';
import type { IValidationError } from '@ben/shared/types/validation';
import { useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import type { IMultiSelectOption, TNestedValue } from '../multi-select-options';
import { MultiSelectOptions } from '../multi-select-options';

interface MultiSelectWithSubformOption {
  key: string;
  values?: Record<string, unknown>;
}

export interface MultiSelectWithSubformProps {
  options: IMultiSelectOption[];
  name: string;
  showName: boolean;
  insideBox: boolean;
  maxSelections: string;
  minSelections: string;
  benefitId: string;
  value: MultiSelectWithSubformOption[];
  onChange: (newValue: MultiSelectWithSubformOption[]) => void;
  handleValidationErrorCallback: (validationError: IValidationError) => void;
  configWindow?: IModularBenefitEnrolmentWindow;
  isConditionalApprovalTriggered?: boolean;
  disabled?: boolean;
}

const MultiSelectWithSubform = ({
  options,
  benefitId,
  handleValidationErrorCallback,
  insideBox,
  maxSelections,
  minSelections,
  name,
  onChange,
  showName,
  value,
  configWindow,
  isConditionalApprovalTriggered,
  disabled,
}: MultiSelectWithSubformProps) => {
  const theme = useTheme();
  const optionsMap = useMemo(() => {
    return options.reduce<Record<string, IMultiSelectOption>>((map, option) => {
      map[option.key] = option;
      return map;
    }, {});
  }, [options]);

  const flattenKeys = useMemo(() => value.map((v) => v.key), [value]);

  const getDefaultValues = useCallback((option?: IMultiSelectOption) => {
    if (!option?.subComponents?.length) return {};

    return Object.fromEntries(
      option.subComponents.map((subComp) => [
        subComp.key,
        subComp.defaultValue ?? subComp.default,
      ])
    );
  }, []);

  const handleChange = useCallback(
    (newKeys: string[]) => {
      const existingValuesMap: Record<
        string,
        Record<string, unknown>
      > = value?.reduce(
        (map: Record<string, Record<string, unknown>>, item) => {
          map[item.key] = item.values || {};
          return map;
        },
        {}
      );

      const newValue = newKeys.map((key) => ({
        key,
        values: existingValuesMap[key] || getDefaultValues(optionsMap[key]),
      }));

      onChange(newValue);
    },
    [value, onChange, optionsMap, getDefaultValues]
  );

  const handleChildValuesChange = useCallback(
    (parentKey: string, newValueKey: string, newValue: TNestedValue) => {
      onChange(
        value.map((item) =>
          item.key === parentKey
            ? { ...item, values: { ...item.values, [newValueKey]: newValue } }
            : item
        )
      );
    },
    [value, onChange]
  );

  const borderColor = insideBox
    ? theme.palette.primary.bg
    : theme.palette.neutral[60];
  const maxSelectionsNumber = maxSelections ? Number(maxSelections) : undefined;
  const minSelectionsNumber = minSelections ? Number(minSelections) : undefined;

  return (
    <MultiSelectOptions
      options={options}
      onChange={handleChange}
      onChildValueChange={handleChildValuesChange}
      borderColor={borderColor}
      values={flattenKeys}
      title={name}
      showTitle={showName}
      max={maxSelectionsNumber}
      min={minSelectionsNumber}
      validationErrorCallback={handleValidationErrorCallback}
      benefitId={benefitId}
      childValues={value.map(({ key, values }) => ({ [key]: values }))}
      configWindow={configWindow}
      isConditionalApprovalTriggered={isConditionalApprovalTriggered}
      disabled={disabled}
    />
  );
};

export default MultiSelectWithSubform;
